import React, { useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormCheck, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "go-form/services/types";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	PaymentMethodIntegration,
	PaymentMethodTPayPaymentsApi,
} from "../../../../../../../../../../../services/Api/Organization/types";
import { getIntegrationName } from "../../../utils/onlinePayments";

interface Props {
	show: boolean;
	onHide: () => void;
	integration: PaymentMethodIntegration;
	setShowIntegrationModal: (show: boolean) => void;
	setTPayPayments: React.Dispatch<React.SetStateAction<PaymentMethodTPayPaymentsApi | undefined>>;
	fetchPaymentMethod?: () => Promise<void>;
}

const PaymentMethodInstallIntegrationModal = ({
	show,
	onHide,
	integration,
	setShowIntegrationModal,
	setTPayPayments,
	fetchPaymentMethod,
}: Props) => {
	const { t } = useTranslation();
	const form = useForm<PaymentMethodTPayPaymentsApi>({
		criteriaMode: "all",
	});
	const {
		formState,
		formState: { errors },
		register,
		setError,
		watch,
		handleSubmit,
	} = form;
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const [loading, setLoading] = useState(false);
	const { addFlash } = useFlash();
	const textInputValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) return;

		setLoading(true);
		try {
			const res = await apiOrganization.installTPayPaymentsIntegration(data);
			if (fetchPaymentMethod) {
				await fetchPaymentMethod();
			}
			setTPayPayments(res);
			setShowIntegrationModal(true);
			onHide();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<Modal show={show} onHide={onHide}>
			<FormDirty formState={formState}>
				<Modal.Header closeButton>
					<Modal.Title>{getIntegrationName(integration, t)}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<FormInput
						name="client_id"
						errors={errors}
						register={register}
						label="Client ID"
						autoComplete="off"
						customValidationConfig={textInputValidationConfig}
					/>
					<FormInput
						name="client_secret"
						errors={errors}
						register={register}
						label="Client Secret"
						autoComplete="off"
						customValidationConfig={textInputValidationConfig}
					/>
					<FormInput
						name="google_merchant_id"
						errors={errors}
						register={register}
						label="Google Merchant ID"
						autoComplete="off"
					/>
					<FormCheck
						type="switch"
						name="apple_pay_on_site"
						errors={errors}
						register={register}
						label={t("modules.payment_method.field.apple_pay_on_site.title")}
					/>
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} onClick={onSubmit}>
						{t("lib:common.action.install")}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("lib:common.action.cancel")}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default PaymentMethodInstallIntegrationModal;
